import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../utils/axiosInstance";
import PaymentMethodForm from "../../pages/admin/SystemValues/PaymentMethods/PaymentMethodForm";
import PaymentMethodList from "../../pages/admin/SystemValues/PaymentMethods/PaymentMethodList";
import ConfirmModal from "../Modals/ConfirmModal";
import BackButton from "../Button/BackButton";
import { toast } from "../Toast/toast";
import { btn } from "../Button/buttonStyles";

/**
 * Reusable PaymentMethodManager component
 * Used in both SystemValues (URL-driven) and SetupWizard (local state).
 */
const PaymentMethodManager = ({
  wizardMode = false,
  onNext = null,
  onBack = null,
}) => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [methodToDelete, setMethodToDelete] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [localAction, setLocalAction] = useState(null);
  const [localEditId, setLocalEditId] = useState(null);

  const action = wizardMode ? localAction : searchParams.get("action");
  const editId = wizardMode ? localEditId : searchParams.get("id");

  const setAction = (nextAction, id) => {
    if (wizardMode) {
      setLocalAction(nextAction);
      setLocalEditId(id ?? null);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    fetchMethods();
  }, []);

  const fetchMethods = async () => {
    try {
      const res = await api.get("/admin-payment-methods");
      setMethods(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        await api.put(`/admin-payment-methods/${editId}`, formData);
      } else {
        await api.post("/admin-payment-methods", formData);
      }

      const res = await api.get("/admin-payment-methods");
      setMethods(res.data.data);
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save payment method");
    }
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;
    try {
      await api.delete(`/admin-payment-methods/${methodToDelete}`);
      setMethods(methods.filter((m) => m.id !== methodToDelete));
      setMethodToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete payment method");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingMethod =
    action === "edit" && editId
      ? methods.find((m) => String(m.id) === String(editId))
      : null;
  const canNext = methods.length > 0;

  return (
    <div
      className={wizardMode ? "flex flex-col h-full bg-white shadow-md rounded-lg" : ""}
    >
      {!wizardMode && (
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Payment Methods</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Payment Method
            </button>
          )}
        </div>
      )}

      {wizardMode && (
        <div className="p-4 border-b">
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900 mb-4 text-center">
            Add Payment Methods
          </h2>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm", "w-full")}
            >
              Add Payment Method
            </button>
          )}
        </div>
      )}

      {error && <p className="text-danger-600 text-sm mb-3 p-4">{error}</p>}

      {inForm ? (
        <div className={wizardMode ? "flex-1 overflow-y-auto p-4" : ""}>
          <PaymentMethodForm
            methodToEdit={editingMethod}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        </div>
      ) : (
        <div className={wizardMode ? "flex-1 overflow-y-auto p-4" : ""}>
          <PaymentMethodList
            methods={methods}
            onEdit={(method) => setAction("edit", method.id)}
            onDelete={(id) => setMethodToDelete(id)}
          />
        </div>
      )}

      {methodToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this payment method?"
          onCancel={() => setMethodToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}

      {wizardMode && (
        <div className="p-4 border-t flex justify-between">
          <button
            onClick={onBack}
            className={btn("primary", "sm")}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={!canNext}
            className={btn("primary", "sm")}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodManager;
